// Every word the Story activity tab shows, in one place for the content team
// to review.

export const HOW_IT_WORKS =
  'Every 15 minutes we group articles about the same event into a story. A story is written once two websites report it, or one official source does, and then waits for review unless its websites publish without review. If something goes wrong, we try again up to 3 times.';

export const PAGE = {
  exchange: 'Exchange',
  exchangeTip:
    "The stock exchange you're working on. Everything on this page is for that exchange only.",
  pickExchange: 'Pick an exchange to see its stories.',
  checkNow: 'Check for new stories now',
  checkNowTip:
    'Reads websites that are due and groups new articles now, instead of waiting up to 15 minutes.',
  checked: (articles, sent) =>
    `Found ${articles} new ${articles === 1 ? 'article' : 'articles'}. ${
      sent
        ? `${sent} ${
            sent === 1 ? 'story is' : 'stories are'
          } ready and have been sent for writing.`
        : 'No story is ready to write yet.'
    }`,
  busy: 'A check is already running. Try again in a minute.',
  empty: 'No stories here yet.',
  emptySearch: 'No stories match your search.',
  emptyWebsite: 'No stories from this website yet.',
  search: 'Search stories',
  searchTip: 'Search by headline or by the website that reported it.',
  website: 'Website',
  websiteTip: 'Show only stories from this website.',
  showing: (from, to, total) =>
    `Showing ${from}–${to} of ${total} ${total === 1 ? 'story' : 'stories'}`,
  pageOf: (total, page, pages) =>
    `${total} ${total === 1 ? 'story' : 'stories'} · page ${page} of ${pages}`,
};

// The chips: which count each shows, and which lists fill it.
export const FILTERS = [
  {
    key: 'all',
    label: 'All',
    tip: 'Every story for this exchange.',
    runs: {},
    clusters: 'ALL',
  },
  {
    key: 'review',
    label: 'Waiting for review',
    count: 'inReview',
    tip: 'Written and waiting for an editor before it goes live.',
    runs: {status: 'IN_REVIEW'},
  },
  {
    key: 'published',
    label: 'Published',
    count: 'published',
    tip: 'Written, translated and live on the website.',
    runs: {status: 'PUBLISHED'},
  },
  {
    key: 'writing',
    label: 'Being written',
    count: 'writing',
    tip: 'With the writing tool now. It usually takes about a minute.',
    runs: {status: 'WRITING'},
  },
  {
    key: 'failed',
    label: "Couldn't publish",
    count: 'failed',
    tip: 'Something went wrong. The reason is on the card, and we try up to 3 times.',
    runs: {status: 'FAILED'},
  },
  {
    key: 'notPublished',
    label: 'Not published',
    count: 'notPublished',
    tip: 'An editor decided not to publish this story.',
    runs: {status: 'NOT_PUBLISHED'},
  },
  {
    key: 'waiting',
    label: 'Waiting for a second website',
    count: 'waiting',
    tip: "Only one website has reported this. It's written if another one reports it within 36 hours.",
    clusters: 'WAITING',
  },
];

export const STATUS = {
  ready: {icon: '→', label: 'Ready', color: 'purple'},
  waiting: {icon: '…', label: 'Waiting for a second website', color: 'default'},
  writing: {icon: '…', label: 'Being written', color: 'purple'},
  review: {icon: '◷', label: 'Waiting for review', color: 'orange'},
  published: {icon: '✓', label: 'Published', color: 'green'},
  notPublished: {icon: '–', label: 'Not published', color: 'default'},
  failed: {icon: '✕', label: "Couldn't publish", color: 'red'},
};

// A status's tooltip is its chip's.
export const STATUS_TIP = {
  ready:
    'Reported by enough websites. It will be sent for writing at the next check.',
  waiting: FILTERS.find(f => f.key === 'waiting').tip,
  writing: FILTERS.find(f => f.key === 'writing').tip,
  review: FILTERS.find(f => f.key === 'review').tip,
  published: FILTERS.find(f => f.key === 'published').tip,
  notPublished: FILTERS.find(f => f.key === 'notPublished').tip,
  failed: FILTERS.find(f => f.key === 'failed').tip,
};

export const SENTENCE = {
  ready: n =>
    `Ready: reported by ${n} websites. It will be sent for writing at the next check, within 15 minutes.`,
  waiting: (outlet, by) =>
    by
      ? `Only ${outlet} has reported this so far. It will be written if another website reports it by ${by}.`
      : `Only ${outlet} has reported this so far. It will be written if another website reports it within 36 hours.`,
  writing: 'With the writing tool now. It usually takes about a minute.',
  review: 'Written and waiting for an editor before it goes live.',
  reviewRequested: 'Written on request and waiting for an editor.',
  notPublished: 'An editor decided not to publish this story.',
  retryAt: (time, left) =>
    `We'll try again automatically at ${time} (${left} ${
      left === 1 ? 'try' : 'tries'
    } left).`,
  retryNext: left =>
    `We'll try again automatically on the next check (${left} ${
      left === 1 ? 'try' : 'tries'
    } left).`,
  gaveUp: "We tried 3 times and couldn't publish this story.",
  needsTech: 'The tech team needs to fix this before it can publish.',
};

// Why a story couldn't publish: what the pipeline saw (failureKind)…
export const FAILURE_KINDS = {
  NOT_ACCEPTED: "We couldn't reach the writing tool.",
  PUBLISH_REFUSED:
    "The story was written but couldn't be saved to the website.",
  NO_RESULT:
    "The writing tool didn't finish this story. The tech team has the details.",
};

// …or, once the writing tool reports it, its own reason (failureCode).
export const FAILURES = {
  TOO_SHORT: ({wordCount, wordLimit}) =>
    `The written story was too short, even after one rewrite${
      wordCount && wordLimit
        ? ` (${wordCount} of at least ${wordLimit} words)`
        : ''
    }.`,
  TOO_LONG: ({wordCount, wordLimit}) =>
    `The written story was too long, even after one rewrite${
      wordCount && wordLimit
        ? ` (${wordCount} of at most ${wordLimit} words)`
        : ''
    }.`,
  BAD_FORMAT: () => "The writing tool returned an answer we couldn't use.",
  TRANSLATION_QUOTA: () =>
    'The translation service has used up its monthly allowance. The tech team needs to top it up.',
  TRANSLATION_FAILED: () => 'The French translation failed.',
  NO_IMAGE: () => 'None of the websites had a picture we could use.',
  PUBLISH_FAILED: () =>
    "The story was written but couldn't be saved to the website.",
};

export const NEEDS_TECH = ['TRANSLATION_QUOTA'];

export const CARD = {
  reportedBy: 'Reported by',
  reportedByTip:
    'The websites whose articles make up this story. Click one to read its original article.',
  view: 'View article',
  viewTip: 'Opens the published story in Website News.',
  review: 'Review it',
  writeNow: 'Write it now',
  writeNowTip:
    "Write this story from one website's report now. It goes to Review first, so you can check it before it's published.",
  writeNowTitle: 'Write this story now?',
  writeNowConfirm:
    "This story has only one source. We'll write it and put it in Review for you to check before it goes live.",
  tryAgain: 'Try again now',
  tryAgainTip:
    "Send this story to be written again now, instead of waiting for the next try. It goes to Review before it's published.",
  tryAgainTitle: 'Try writing this story again now?',
  tryAgainConfirm:
    "We'll send it to the writing tool again and put the result in Review for you to check.",
  sentForWriting: 'Being written. It will be in Review in about a minute.',
  reviewTip: 'Opens the draft in Review, where you can publish it.',
  tries: (n, max) => `Try ${n} of ${max}`,
  triesTip: 'We try each story up to 3 times before giving up.',
  technical: 'Technical details',
  storyId: 'Story ID',
  run: 'Writing tool run',
  error: 'Error',
};
